import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, ErrorModal, useResponsive } from '@common-fe/common-fe';

import { MobilePageModalWrapper, ModalWrapper } from '@/components';
import { SnackbarState } from '@/modules/core/core.types';
import { useSnackbar } from '@/modules/core/hooks';
import AddPatientForm from '@/modules/transaction/components/ReimburseMe/ClaimInfo/AddPatienForm/AddPatientForm';
import { PatientPayload } from '@/modules/transaction/components/ReimburseMe/ClaimInfo/usePatients.query';
import useHighlightedDependentStore from '@/modules/user/components/ManageProfile/components/Dependents/stores/useHighlightedDependent.store';
import useHighlightById from '@/utils/hooks/useHighlightById';
import useSsnConflictError from '@/utils/hooks/useSsnConflictError';

interface Props {
  onSetVisible: (value: boolean) => void;
}

const AddDependentModal: React.FC<Props> = ({ onSetVisible }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { handleAddPermanentSnackbar } = useSnackbar();
  const { reset, setHighlightedDependentId } = useHighlightedDependentStore();
  const { setHighlightedById } = useHighlightById({ onSetHighlightedId: setHighlightedDependentId, reset });
  const [isSsnConflictVisible, setSsnConflictVisible] = useState(false);
  const handleSsnConflictError = useSsnConflictError({ setSsnConflictVisible });

  const handleClose = useCallback(() => {
    onSetVisible(false);
  }, [onSetVisible]);

  const onSuccess = useCallback(
    (dependent?: PatientPayload) => {
      const dependentId = `${dependent?.dependent_id}`;
      handleAddPermanentSnackbar({
        text: `${dependent?.first_name} ${dependent?.last_name} was added successfully!`,
        state: SnackbarState.positive,
        closeIcon: true,
        autoRemove: true,
      });
      if (dependentId) {
        setHighlightedById(dependentId);
      }
      setTimeout(() => handleClose());
    },
    [setHighlightedById, handleAddPermanentSnackbar, handleClose]
  );
  const form = (
    <AddPatientForm
      onCancel={handleClose}
      onSuccess={onSuccess}
      onError={handleSsnConflictError}
      submitButtonText="Add Dependent"
    />
  );

  if (isMobile) {
    return (
      <>
        <MobilePageModalWrapper title={t('Add a Dependent')} isVisible onSetVisible={() => handleClose()}>
          <Box pad={{ horizontal: 'spacing2' }} style={{ flex: 1 }}>
            {form}
          </Box>
        </MobilePageModalWrapper>
        <ErrorModal
          header={t('SSN must be unique')}
          buttonText={t('Got It!')}
          visible={isSsnConflictVisible}
          onSetVisible={setSsnConflictVisible}
        />
      </>
    );
  }
  return (
    <>
      <ModalWrapper
        visible
        title={t('Add a Dependent')}
        onSetVisible={onSetVisible}
        testId="AddDependent"
        isClosingByClickOutsideBlocked={isSsnConflictVisible}
      >
        {form}
      </ModalWrapper>
      <ErrorModal
        header={t('SSN must be unique')}
        buttonText={t('Got It!')}
        visible={isSsnConflictVisible}
        onSetVisible={setSsnConflictVisible}
      />
    </>
  );
};

export default AddDependentModal;
